import {
  useCallback,
  useState,
  type MouseEvent,
  type MutableRefObject,
} from "react";
import { add, getMonth, isAfter } from "date-fns";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { Button } from "@components/Button";
import { ChevronLeftIcon, ChevronRightIcon } from "@components/icons";
import { formatDate } from "@utils/units";
import { type IMonthPickerAvailableDate } from "../MonthPicker";

interface IMonthPickerContentProps {
  value: Date;
  close: (
    focusableElement?:
      | HTMLElement
      | MutableRefObject<HTMLElement | null>
      | MouseEvent<HTMLElement>,
  ) => void;
  onChange: (value: Date, dateInformation?: IMonthPickerAvailableDate) => void;
  availableDates?: IMonthPickerAvailableDate[];
  disableBefore?: Date;
  disableFutureDates?: boolean;
}

export const MonthPickerContent: React.FC<IMonthPickerContentProps> = (
  props,
) => {
  const {
    value,
    close,
    onChange,
    availableDates,
    disableBefore,
    disableFutureDates,
  } = props;
  const { t } = useTranslation();

  const [selectedDate, setSelectedDate] = useState(value);
  const [currentDatePage, setCurrentDatePage] = useState(value);

  const selectedYear = formatDate(selectedDate, "yyyy");
  const selectedMonth = getMonth(selectedDate);
  const currentYear = formatDate(currentDatePage, "yyyy");

  const handlePageChange = (yearsToAdd: number) => {
    setCurrentDatePage((prev) => add(prev, { years: yearsToAdd }));
  };

  const isMonthDisabled = useCallback(
    (year: number, monthIndex: number) => {
      let disabled = false;

      if (availableDates) {
        disabled = !availableDates.some(
          (date) => date.year === year && date.month - 1 === monthIndex,
        );
      }

      if (disableFutureDates) {
        disabled = disabled || isAfter(new Date(year, monthIndex), new Date());
      }

      if (disableBefore) {
        disabled =
          disabled || isAfter(disableBefore, new Date(year, monthIndex + 1, 0));
      }

      return disabled;
    },
    [availableDates, disableFutureDates, disableBefore],
  );

  return (
    <div className="flex flex-col gap-4 pb-4 pt-6">
      <div className="flex items-center justify-between px-4">
        <span className="text-base font-semibold">
          {formatDate(currentDatePage, t("common.dateFormat.year"))}
        </span>
        <div className="flex items-center gap-4">
          <button onClick={() => handlePageChange(-1)}>
            <ChevronLeftIcon className="size-6" />
          </button>
          <button onClick={() => handlePageChange(1)}>
            <ChevronRightIcon className="size-6" />
          </button>
        </div>
      </div>
      <div className="grid w-72 grid-cols-4 gap-y-1 px-4">
        {Array.from({ length: 12 }).map((_, index) => (
          <button
            disabled={isMonthDisabled(parseInt(currentYear), index)}
            key={index}
            className={twMerge(
              "block flex-1 rounded-full text-center text-sm leading-9 hover:bg-grey-hover disabled:text-grey-light disabled:hover:bg-white",
              selectedMonth === index &&
                selectedYear === currentYear &&
                "bg-blue-dark text-white hover:bg-blue-dark",
            )}
            onClick={() =>
              setSelectedDate(new Date(Number(currentYear), index))
            }
          >
            {t(`monthPicker.${index}`)}
          </button>
        ))}
      </div>
      <div className="flex gap-3 border-t border-grey-light px-4 pt-4">
        <Button className="w-full" variant="secondary" onClick={close}>
          {t("common.form.buttons.cancel")}
        </Button>
        <Button
          className="w-full"
          onClick={() => {
            const dateInformation = availableDates?.find(
              (date) =>
                date.year === parseInt(selectedYear) &&
                date.month - 1 === selectedMonth,
            );

            onChange(selectedDate, dateInformation);
            close();
          }}
        >
          {t("common.form.buttons.confirm")}
        </Button>
      </div>
    </div>
  );
};
